import type {
  AdminContactMessageView,
  AdminSubmissionView,
  AdminUserView,
  AuditLogView,
  PublicRuntimeConfig,
  SubmissionStatus,
  SubmissionView,
  UploadIntent,
  UserProfileView,
  UserRole,
  VideoView,
} from "@growthwise/core";
import { getApiBaseUrl, loadAuthProvider } from "../adapters/registry";

/**
 * Typed client for the GrowthWise API.
 *
 * All business data goes through this one object. The page components never
 * read or write a database directly, which is what lets the data layer be
 * replaced without touching the UI.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fieldErrors: Record<string, string[]>;
  readonly details: Record<string, unknown>;

  constructor(
    code: string,
    status: number,
    message: string,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
    const nested = details["fieldErrors"];
    this.fieldErrors =
      typeof nested === "object" && nested !== null
        ? (nested as Record<string, string[]>)
        : {};
  }

  /** True when the caller's session is gone or no longer valid. */
  get isSessionProblem(): boolean {
    return this.status === 401;
  }
}

export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super("network_error");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** Attach the bearer token. Off for the public endpoints. */
  authenticated?: boolean;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

const buildUrl = (path: string, query?: RequestOptions["query"]): string => {
  const base = `${getApiBaseUrl()}${path}`;
  if (!query) return base;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `${base}?${serialized}` : base;
};

const request = async <T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> => {
  const headers: Record<string, string> = { Accept: "application/json" };

  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  if (options.authenticated !== false) {
    const token = await (await loadAuthProvider()).getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      body:
        options.body === undefined ? undefined : JSON.stringify(options.body),
      // The API authenticates with a bearer token, so no cookie is sent and
      // there is no CSRF surface to defend.
      credentials: "omit",
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    throw new NetworkError(error);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    const envelope =
      typeof payload === "object" && payload !== null && "error" in payload
        ? (
            payload as {
              error: {
                code?: string;
                message?: string;
                details?: Record<string, unknown>;
              };
            }
          ).error
        : null;

    throw new ApiError(
      envelope?.code ?? "internal_error",
      response.status,
      envelope?.message ?? response.statusText,
      envelope?.details ?? {},
    );
  }

  return payload as T;
};

const safeJsonParse = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

interface PageResponse<T> {
  items: T[];
  nextCursor: string | null;
}

export const api = {
  // ------------------------------------------------------------- public

  getPublicConfig: (): Promise<PublicRuntimeConfig> =>
    request("/config", { authenticated: false }),

  submitContactForm: (body: {
    name: string;
    email: string;
    serviceInterest: string;
    audienceType: string | null;
    organization: string | null;
    message: string;
    locale: string;
    consentToContact: true;
    clientRequestId: string;
    antiAbuseToken: string | null;
    website?: string;
  }): Promise<{ id: string; deduplicated: boolean }> =>
    request("/contact", { method: "POST", body, authenticated: false }),

  // ------------------------------------------------------------ account

  getProfile: (): Promise<{ user: UserProfileView }> => request("/me"),

  updateProfile: (body: {
    displayName?: string;
    organization?: string | null;
    contactPhone?: string | null;
    preferredLanguage?: string;
  }): Promise<{ user: UserProfileView }> =>
    request("/me", { method: "PATCH", body }),

  listSubmissions: (cursor?: string): Promise<PageResponse<SubmissionView>> =>
    request("/submissions", { query: { cursor, limit: 20 } }),

  getSubmission: (id: string): Promise<{ submission: SubmissionView }> =>
    request(`/submissions/${encodeURIComponent(id)}`),

  createSubmission: (body: {
    data: unknown;
    clientRequestId: string;
  }): Promise<{ submission: SubmissionView }> =>
    request("/submissions", { method: "POST", body }),

  updateSubmission: (
    id: string,
    data: unknown,
  ): Promise<{ submission: SubmissionView }> =>
    request(`/submissions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { data },
    }),

  submitSubmission: (id: string): Promise<{ submission: SubmissionView }> =>
    request(`/submissions/${encodeURIComponent(id)}/submit`, {
      method: "POST",
      body: {},
    }),

  deleteSubmission: (
    id: string,
  ): Promise<{ deleted: true; videosDeleted: number; videosPending: number }> =>
    request(`/submissions/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // ------------------------------------------------------------- videos

  listVideos: (cursor?: string): Promise<PageResponse<VideoView>> =>
    request("/videos", { query: { cursor, limit: 20 } }),

  createUploadIntent: (body: {
    submissionId: string | null;
    mimeType: string;
    source: "browser_recording" | "file_upload";
    clientRequestId: string;
    expectedSizeBytes: number | null;
  }): Promise<{ intent: UploadIntent }> =>
    request("/videos/upload-intent", { method: "POST", body }),

  completeUpload: (
    id: string,
    body: { durationMs: number | null; reportedSizeBytes: number | null },
  ): Promise<{ video: VideoView }> =>
    request(`/videos/${encodeURIComponent(id)}/complete`, {
      method: "POST",
      body,
    }),

  abortUpload: (id: string): Promise<void> =>
    request(`/videos/${encodeURIComponent(id)}/abort`, {
      method: "POST",
      body: {},
    }),

  getVideoPlayback: (id: string): Promise<{ url: string; expiresAt: string }> =>
    request(`/videos/${encodeURIComponent(id)}/playback`),

  deleteVideo: (
    id: string,
  ): Promise<{ status: "deleted" | "delete_pending" }> =>
    request(`/videos/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // -------------------------------------------------------------- admin

  admin: {
    listSubmissions: (query: {
      status?: string;
      email?: string;
      from?: string;
      to?: string;
      cursor?: string;
    }): Promise<PageResponse<AdminSubmissionView>> =>
      request("/admin/submissions", { query: { ...query, limit: 20 } }),

    getSubmission: (
      id: string,
    ): Promise<{ submission: AdminSubmissionView; videos: VideoView[] }> =>
      request(`/admin/submissions/${encodeURIComponent(id)}`),

    setSubmissionStatus: (
      id: string,
      body: { status: SubmissionStatus; note: string | null },
    ): Promise<{ submission: AdminSubmissionView }> =>
      request(`/admin/submissions/${encodeURIComponent(id)}/status`, {
        method: "PATCH",
        body,
      }),

    addNote: (
      id: string,
      body: string,
    ): Promise<{ submission: AdminSubmissionView }> =>
      request(`/admin/submissions/${encodeURIComponent(id)}/notes`, {
        method: "POST",
        body: { body },
      }),

    deleteSubmission: (
      id: string,
    ): Promise<{
      deleted: true;
      videosDeleted: number;
      videosPending: number;
    }> =>
      request(`/admin/submissions/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),

    getVideoPlayback: (
      id: string,
    ): Promise<{ url: string; expiresAt: string }> =>
      request(`/admin/videos/${encodeURIComponent(id)}/playback`),

    deleteVideo: (
      id: string,
    ): Promise<{ status: "deleted" | "delete_pending" }> =>
      request(`/admin/videos/${encodeURIComponent(id)}`, { method: "DELETE" }),

    listUsers: (query: {
      role?: UserRole;
      q?: string;
      cursor?: string;
    }): Promise<PageResponse<AdminUserView>> =>
      request("/admin/users", { query: { ...query, limit: 20 } }),

    setUserRole: (
      id: string,
      role: UserRole,
    ): Promise<{ user: AdminUserView }> =>
      request(`/admin/users/${encodeURIComponent(id)}/role`, {
        method: "POST",
        body: { role },
      }),

    setUserDisabled: (
      id: string,
      disabled: boolean,
    ): Promise<{ user: AdminUserView }> =>
      request(`/admin/users/${encodeURIComponent(id)}/disabled`, {
        method: "POST",
        body: { disabled },
      }),

    listContactMessages: (query: {
      status?: string;
      email?: string;
      cursor?: string;
    }): Promise<PageResponse<AdminContactMessageView>> =>
      request("/admin/contact-messages", { query: { ...query, limit: 20 } }),

    setContactMessageStatus: (
      id: string,
      status: string,
    ): Promise<{ message: AdminContactMessageView }> =>
      request(`/admin/contact-messages/${encodeURIComponent(id)}/status`, {
        method: "PATCH",
        body: { status },
      }),

    listAuditLogs: (query: {
      action?: string;
      targetId?: string;
      cursor?: string;
    }): Promise<PageResponse<AuditLogView>> =>
      request("/admin/audit-logs", { query: { ...query, limit: 30 } }),

    /**
     * Downloads the CSV export.
     *
     * Handled outside `request` because the response is a file, and because
     * the download must carry the Authorization header -- a plain anchor
     * link could not, which would mean exposing an unauthenticated export
     * URL.
     */
    exportSubmissionsCsv: async (query: {
      status?: string;
      email?: string;
      from?: string;
      to?: string;
    }): Promise<{ blob: Blob; filename: string }> => {
      const token = await (await loadAuthProvider()).getAccessToken();
      const response = await fetch(
        buildUrl("/admin/submissions/export.csv", query),
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "omit",
        },
      );

      if (!response.ok) {
        const text = await response.text();
        const payload = safeJsonParse(text);
        const envelope =
          typeof payload === "object" && payload !== null && "error" in payload
            ? (payload as { error: { code?: string; message?: string } }).error
            : null;
        throw new ApiError(
          envelope?.code ?? "internal_error",
          response.status,
          envelope?.message ?? response.statusText,
        );
      }

      const disposition = response.headers.get("content-disposition") ?? "";
      const match = /filename="([^"]+)"/.exec(disposition);
      return {
        blob: await response.blob(),
        filename: match?.[1] ?? "growthwise-submissions.csv",
      };
    },

    bootstrap: (body: {
      bootstrapSecret: string;
      email: string;
    }): Promise<{ granted: true; appUserId: string; email: string }> =>
      request("/admin/bootstrap", {
        method: "POST",
        body,
        authenticated: false,
      }),
  },
};

/**
 * Generates an idempotency key for a write.
 *
 * Created once when a form is opened and reused across retries, so a
 * double-click or an automatic network retry resolves to the same record
 * instead of creating a second one.
 */
export const newClientRequestId = (): string => {
  const globalCrypto = globalThis.crypto;
  if (globalCrypto && "randomUUID" in globalCrypto) {
    return globalCrypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};
